import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock, Eye, EyeOff, FileText, Loader2, ShieldCheck } from 'lucide-react';
import { DocImg, DocUpload, DocViewer, FieldRow, Label, RemarkBox, SelectField, Stage, SubmitNotice, inputCls } from './shared';
import { ocrService } from '../../../../services/api';
import { KYC_OCR_LABELS, KycDocType, KycOcrFields, parseKycOcr } from '../../../../utils/kycOcr';
import { toDdmmyyyy } from '../../../../utils/format';
import { DatePicker } from '../../../common/DatePicker';

// One form for both the KYC and the Bank section. Which keys are editable mirrors
// utils/kyc_flow.editable_keys on the server: everything while in DRAFT, only the
// flagged keys after submission, nothing once acknowledged. The server re-checks.

type Kind = 'text' | 'name' | 'email' | 'date' | 'select' | 'aadhar' | 'pan' | 'pincode' | 'textarea' | 'account' | 'ifsc' | 'doc';

export interface FieldDef {
    key: string;
    label: string;
    kind: Kind;
    placeholder?: string;
    options?: string[];
    hint?: string;
    full?: boolean;
    folder?: string;
    // Checkboxes that copy another field's value (e.g. "Same as Aadhaar address");
    // while ticked the field follows that one and can't be typed in.
    sameAs?: { key: string; label: string }[];
    // Most characters the field takes; typing stops there.
    maxLength?: number;
    // An ID card upload whose details are read with OCR into the form's other fields.
    ocr?: KycDocType;
}

// required: the group as a whole is mandatory (e.g. bank proof — at least one of its
// uploads); its heading shows on every screen with a *, and its fields drop "(optional)".
// heading / icon: the card header shown on mobile (title stays the desktop heading).
// showIf: the group shows only while this holds for the form's current values.
export interface GroupDef { title: string; fields: FieldDef[]; required?: boolean; note?: string; heading?: string; icon?: React.ReactNode; showIf?: (values: Record<string, any>) => boolean }

const sanitize = (kind: Kind, v: string) => {
    switch (kind) {
        case 'name': return v.replace(/[^A-Za-z .]/g, '');
        case 'aadhar': return v.replace(/\D/g, '').slice(0, 12);
        case 'pan': return v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
        case 'pincode': return v.replace(/\D/g, '').slice(0, 6);
        case 'account': return v.replace(/\D/g, '').slice(0, 18);
        case 'ifsc': return v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
        default: return v;
    }
};

// Today in the viewer's time zone, as yyyy-mm-dd (toISOString would give the UTC date).
const todayLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const clientError = (kind: Kind, v: string, required: boolean): string | undefined => {
    const s = (v || '').trim();
    if (!s) return required ? 'This field is required' : undefined;
    if (kind === 'aadhar' && !/^[2-9]\d{11}$/.test(s)) return 'Enter a valid 12-digit Aadhaar number';
    if (kind === 'pan' && !/^[A-Z]{5}\d{4}[A-Z]$/.test(s)) return 'Format: ABCDE1234F';
    if (kind === 'pincode' && !/^[1-9]\d{5}$/.test(s)) return 'Enter a valid 6-digit PIN code';
    if (kind === 'account' && (s.length < 9 || s.length > 18)) return 'Account number must be 9 to 18 digits';
    if (kind === 'ifsc' && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(s)) return 'Enter a valid 11-character IFSC';
    if (kind === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s)) return 'Enter a valid email';
    if (kind === 'name' && !/^[A-Za-z][A-Za-z .]*$/.test(s)) return 'Use letters only';
    if (kind === 'date' && s > todayLocal()) return 'Date cannot be after today';
    return undefined;
};

const display = (def: FieldDef, v: any) => {
    if (v === null || v === undefined || v === '') return '—';
    if (def.kind === 'date') {
        return toDdmmyyyy(String(v));
    }
    return String(v);
};

const toInputDate = (v: any) => {
    if (!v) return '';
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
};

const StateBadge: React.FC<{ row: FieldRow; stage: Stage }> = ({ row, stage }) => {
    if (row.verify_state === 'n/a' || !row.value) return null;
    // While filling in, only staff outcomes are worth a badge; "pending" is just a saved value.
    if (stage === 'DRAFT' && row.verify_state === 'pending') return null;
    if (row.verify_state === 'verified') return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-100 px-2 h-6 rounded-full"><CheckCircle2 size={12} /> Verified</span>;
    if (row.verify_state === 'flagged') return <span className="text-[11px] font-bold text-red-800 bg-red-100 px-2 h-6 rounded-full inline-flex items-center">Needs fix</span>;
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-900 bg-amber-100 px-2 h-6 rounded-full"><Clock size={12} /> In review</span>;
};

interface Props {
    stage: Stage;
    // Staff filling in for the investor (same editing rules; may paste document links).
    assist?: boolean;
    rows: FieldRow[];
    groups: GroupDef[];
    mobile: string;
    saving: boolean;
    serverErrors: Record<string, string>;
    extra?: (values: Record<string, any>, set: (k: string, v: any) => void, editable: Set<string>) => React.ReactNode;
    extraErrors?: (values: Record<string, any>) => Record<string, string>;
    onSave: (values: Record<string, any>) => void;
    saveLabel: string;
    footerLeft?: React.ReactNode;
    // Shown under the sticky mobile save button, e.g. "Draft saved 2 min ago".
    savedNote?: string;
    // Content shown under an editable field, inside its own cell (all screen sizes).
    // When it returns something, that field's hint is hidden.
    fieldAfter?: (key: string, values: Record<string, any>, editable: Set<string>) => React.ReactNode;
    // Mobile only: fields to hide unless they have an error (e.g. bank name filled from IFSC).
    mobileHiddenKeys?: string[];
    // Live required rule from the values being typed (e.g. PAN only for adults);
    // undefined keeps the server's flag.
    requiredOverride?: (key: string, values: Record<string, any>) => boolean | undefined;
    // One "submit" button instead of Save: enabled once every required field is
    // filled; receives the changed values to save before submitting.
    submit?: { label: string; onSubmit: (changed: Record<string, any>) => void };
    // Section verified by staff: shown read-only, nothing to save or correct.
    readOnly?: boolean;
    // Suggested values for editable fields with nothing on file (e.g. account holder
    // name from the KYC name). Not saved yet, so a save sends them like typed values.
    prefill?: Record<string, string>;
}

const formatMobile = (m: string) => {
    const d = String(m || '').replace(/\D/g, '').slice(-10);
    return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : m;
};

const FieldSection: React.FC<Props> = ({ stage, assist, rows, groups, mobile, saving, serverErrors, extra, extraErrors, onSave, saveLabel, footerLeft, savedNote, fieldAfter, mobileHiddenKeys, requiredOverride, submit, readOnly, prefill }) => {
    const byKey = useMemo(() => Object.fromEntries(rows.map(r => [r.key, r])), [rows]);
    const editable = useMemo(() => {
        if (readOnly) return new Set<string>();
        // Fields staff already verified lock even before the profile is submitted.
        // Staff filling in for the investor get the investor's rules: once submitted,
        // what's in review locks (staff change it with Edit on the verification page).
        if (stage === 'DRAFT') return new Set(rows.filter(r => r.verify_state !== 'verified').map(r => r.key));
        if (stage === 'SUBMITTED') return new Set(rows.filter(r => r.verify_state === 'flagged' || r.empty).map(r => r.key));
        return new Set<string>();
    }, [rows, stage, readOnly]);
    const initial = useMemo(() => {
        const out: Record<string, any> = {};
        for (const r of rows) {
            const def = groups.flatMap(g => g.fields).find(f => f.key === r.key);
            out[r.key] = def?.kind === 'date' ? toInputDate(r.value) : (r.value ?? '');
        }
        return out;
    }, [rows, groups]);
    const withPrefill = useCallback(() => {
        const out = { ...initial };
        for (const [k, v] of Object.entries(prefill || {})) if (v && editable.has(k) && !out[k]) out[k] = v;
        return out;
    }, [initial, prefill, editable]);
    const [values, setValues] = useState<Record<string, any>>(withPrefill);
    const [touched, setTouched] = useState(false);
    // Fields typed in since load — checked as they're typed, like the staff forms.
    const [dirty, setDirty] = useState<Set<string>>(new Set());
    const [reveal, setReveal] = useState(false);
    useEffect(() => { setValues(withPrefill()); setTouched(false); setDirty(new Set()); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps
    // A server error (e.g. Aadhaar already registered) stays until that field is changed.
    const [serverVals, setServerVals] = useState<Record<string, any>>({});
    useEffect(() => { setServerVals(values); }, [serverErrors]); // eslint-disable-line react-hooks/exhaustive-deps
    // "Same as" links: field key -> the field it copies. Ticked on load when the saved values already match.
    const [linked, setLinked] = useState<Record<string, string>>({});
    useEffect(() => {
        const out: Record<string, string> = {};
        for (const f of groups.flatMap(g => g.fields)) {
            const src = f.sameAs?.find(s => initial[f.key] && String(initial[f.key]) === String(initial[s.key] ?? ''));
            if (src) out[f.key] = src.key;
        }
        setLinked(out);
    }, [initial, groups]);
    // A linked field follows its source as that is edited (chains settle over a render or two).
    useEffect(() => {
        const next = Object.entries(linked).filter(([k, src]) => editable.has(k) && (values[k] ?? '') !== (values[src] ?? ''));
        if (next.length) setValues(prev => ({ ...prev, ...Object.fromEntries(next.map(([k, src]) => [k, prev[src] ?? ''])) }));
    }, [values, linked, editable]);
    // OCR on an uploaded ID card: fills the fields it finds that the investor may still edit.
    // Per document: reading, or what it filled ([] = nothing could be read).
    const [ocr, setOcr] = useState<Record<string, 'reading' | string[]>>({});
    const readDoc = async (docKey: string, type: KycDocType, file: File) => {
        setOcr(prev => ({ ...prev, [docKey]: 'reading' }));
        const res = await ocrService.extractText(file);
        const found = res?.text ? parseKycOcr(res.text, type) : {};
        const fill = (Object.entries(found) as [keyof KycOcrFields, string][]).filter(([k]) => editable.has(k) && byKey[k]);
        for (const [k, v] of fill) set(k, v);
        setOcr(prev => ({ ...prev, [docKey]: fill.map(([k]) => KYC_OCR_LABELS[k]) }));
    };
    const toggleSame = (k: string, src: string, on: boolean) => {
        setLinked(prev => { const n = { ...prev }; if (on) n[k] = src; else delete n[k]; return n; });
        if (on) set(k, values[src] ?? '');
    };

    const isRequired = (key: string) => requiredOverride?.(key, values) ?? byKey[key]?.required ?? false;
    // After submission a field that was empty may be filled, but needn't be — the
    // investor can send whichever remaining details they have; a flagged field
    // (it has a value) must stay filled.
    const mustFill = (key: string) => (stage === 'DRAFT' ? isRequired(key) : !byKey[key]?.empty);
    // After submission: was anything entered (a remaining field filled, or a flagged one changed)?
    const entered = (key: string) => String(values[key] ?? '').trim() !== String(initial[key] ?? '').trim();
    const set = (k: string, v: any) => {
        setValues(prev => ({ ...prev, [k]: v }));
        setDirty(prev => (prev.has(k) ? prev : new Set(prev).add(k)));
    };
    const errors: Record<string, string> = Object.fromEntries(
        Object.entries(serverErrors).filter(([k]) => String(values[k] ?? '') === String(serverVals[k] ?? '')));
    if (!touched) {
        // While typing: a field's format errors show (and clear) live; "required" waits for Save.
        for (const g of groups) for (const f of g.fields) {
            if (!editable.has(f.key) || !dirty.has(f.key) || f.kind === 'doc' || errors[f.key]) continue;
            const e = clientError(f.kind, String(values[f.key] ?? ''), false);
            if (e) errors[f.key] = e;
        }
    } else {
        for (const g of groups) for (const f of g.fields) {
            if (!editable.has(f.key)) continue;
            const e = f.kind === 'doc' ? (mustFill(f.key) && !values[f.key] ? 'Please upload this document' : undefined)
                : clientError(f.kind, String(values[f.key] ?? ''), mustFill(f.key));
            if (e) errors[f.key] = e;
        }
        Object.assign(errors, extraErrors ? extraErrors(values) : {});
    }

    // Validates and returns the values to send, or null when something needs fixing.
    const collect = (): Record<string, any> | null => {
        setTouched(true);
        const changed: Record<string, any> = {};
        let bad = false;
        for (const g of groups) for (const f of g.fields) {
            if (!editable.has(f.key)) continue;
            const v = values[f.key];
            const e = f.kind === 'doc' ? (mustFill(f.key) && !v ? 'x' : undefined) : clientError(f.kind, String(v ?? ''), mustFill(f.key));
            if (e) bad = true;
            // DRAFT: send what changed. After submit: send every flagged field so the
            // server records it as resubmitted even if the value is unchanged, plus the
            // remaining (empty) fields the investor filled — ones left empty aren't sent.
            if (stage === 'DRAFT' ? String(v ?? '') !== String(initial[f.key] ?? '')
                : !byKey[f.key]?.empty || String(v ?? '').trim() !== '') changed[f.key] = v === '' ? null : v;
        }
        if (extraErrors && Object.keys(extraErrors(values)).length) bad = true;
        return bad ? null : changed;
    };
    const save = () => { const c = collect(); if (c) onSave(c); };
    const send = () => { const c = collect(); if (c && submit) submit.onSubmit(c); };

    const nothingEditable = editable.size === 0;
    const canEditKey = (k: string) => editable.has(k);
    // Required editable fields still empty, plus section-level checks (account confirm, proof).
    const left = groups.flatMap(g => g.fields).filter(f => editable.has(f.key) && isRequired(f.key) && !String(values[f.key] ?? '').trim()).length
        + Object.keys(extraErrors ? extraErrors(values) : {}).length;
    // After submission the button waits until at least one remaining detail is entered.
    const nothingEntered = stage !== 'DRAFT' && !groups.flatMap(g => g.fields).some(f => editable.has(f.key) && entered(f.key));
    const footNote = submit
        ? (left ? `Fill ${left} more required item${left === 1 ? '' : 's'} to submit` : 'Everything is filled in · ready to submit')
        : nothingEntered ? 'Enter at least one detail to submit'
        : [left ? `${left} item${left === 1 ? '' : 's'} left` : 'All filled in', savedNote].filter(Boolean).join(' · ');

    return (
        <div>
            {/* After submitting: what's sent is in review and locked; what isn't (still empty) can be
                filled and submitted; anything staff flag opens again to fix. */}
            {stage === 'SUBMITTED' && !nothingEditable && (
                rows.some(r => editable.has(r.key) && r.verify_state === 'flagged')
                    ? <div className="mt-4 p-3.5 rounded-xl bg-red-50 text-red-900 text-sm">Fix the fields marked <b>Needs fix</b> and fill in anything still missing. Details already in review stay locked.</div>
                    : <div className="mt-4 p-3.5 rounded-xl bg-amber-50 text-amber-950 text-sm">Details you've submitted are <b>in review</b> and locked. Fill in what's still empty below and submit it for review.</div>
            )}
            {submit && !nothingEditable && <SubmitNotice className="mt-4 max-lg:mt-3" />}
            {groups.filter(g => !g.showIf || g.showIf(values)).map(g => (
                <section key={g.title} className="mt-6 max-lg:mt-4 max-lg:p-5 max-lg:bg-white max-lg:rounded-3xl max-lg:border max-lg:border-slate-200">
                    {/* Mobile: card header — icon tile, title, and the group's note if it has one. */}
                    <div className="lg:hidden flex items-center gap-3.5 mb-5">
                        <span className="w-12 h-12 rounded-2xl bg-slate-100 text-[#1B4332] grid place-items-center shrink-0">{g.icon}</span>
                        <div className="min-w-0">
                            <h3 className="text-lg font-extrabold text-[#14231B] leading-tight">{g.heading || g.title}{g.required && <span className="text-red-600 ml-0.5">*</span>}</h3>
                            {g.note && <p className={`mt-0.5 text-[13px] ${errors[g.fields[0]?.key] ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>{g.note}</p>}
                        </div>
                    </div>
                    {g.required ? (
                        <div className="max-lg:hidden">
                            <h3 className="text-[15px] font-extrabold text-[#14231B]">{g.title}<span className="text-red-600 ml-0.5">*</span></h3>
                            {g.note && <p className={`mt-0.5 mb-3 text-[13px] ${errors[g.fields[0]?.key] ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>{g.note}</p>}
                        </div>
                    ) : (
                        <h3 className="text-xs font-extrabold tracking-[.08em] uppercase text-[#3F5F4C] mb-3 max-lg:hidden">{g.title}</h3>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {g.fields.map(f => {
                            const row = byKey[f.key];
                            if (!row) return null;
                            const canEdit = editable.has(f.key);
                            const err = errors[f.key];
                            const id = `f-${f.key}`;
                            const flaggedCls = row.verify_state === 'flagged' && stage !== 'ACKNOWLEDGED' ? 'p-3.5 rounded-2xl ring-2 ring-red-300 bg-white' : '';
                            const after = canEditKey(f.key) ? fieldAfter?.(f.key, values, editable) : null;
                            const hideCls = mobileHiddenKeys?.includes(f.key) && !err ? 'max-lg:hidden' : '';
                            const mobileRow = f.key === 'last_name' && mobile ? (
                                <div>
                                    <span className="block text-[13px] max-lg:text-[15px] font-bold text-slate-700 max-lg:text-[#14231B] mb-1.5 max-lg:mb-2">Mobile number</span>
                                    <div className="h-12 max-lg:h-14 px-4 rounded-xl bg-slate-50 border border-slate-300 flex items-center gap-3 text-base font-semibold text-slate-800 tabular-nums">
                                        <span aria-hidden="true">🇮🇳</span><span>+91</span><span className="w-px h-6 bg-slate-300" />{formatMobile(mobile).replace(/^\+91\s*/, '')}
                                    </div>
                                    <p className="mt-1 text-xs text-slate-500">Verified by OTP</p>
                                </div>
                            ) : null;
                            if (!canEdit) {
                                return (<React.Fragment key={f.key}>
                                    <div className={`${f.full || f.kind === 'textarea' ? 'sm:col-span-2' : ''} ${hideCls}`}>
                                        <div className="flex items-center justify-between gap-2 mb-1.5">
                                            <span className="text-[13px] font-bold text-slate-700">{f.label}</span>
                                            <StateBadge row={row} stage={stage} />
                                        </div>
                                        {f.kind === 'doc' ? (
                                            row.value ? (
                                                <DocViewer url={row.value} label={f.label}
                                                    className="block w-full h-44 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                                                    {String(row.value).toLowerCase().includes('.pdf')
                                                        ? <span className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-[#1B4332]"><FileText size={32} /><span className="text-xs font-bold">PDF · tap to view</span></span>
                                                        : <DocImg src={row.value} alt={f.label} className="w-full h-full object-contain" />}
                                                </DocViewer>
                                            ) : <div className="h-12 px-3.5 rounded-xl border border-slate-200 bg-slate-50 flex items-center text-sm text-slate-400">Not uploaded</div>
                                        ) : (
                                            <div className={`px-3.5 rounded-xl border border-slate-200 bg-slate-50 text-[15px] text-slate-800 break-words ${f.kind === 'textarea' ? 'py-3 min-h-[48px] whitespace-pre-wrap' : 'h-12 flex items-center'} ${['aadhar', 'pan', 'account', 'ifsc', 'pincode'].includes(f.kind) ? 'tabular-nums tracking-wide' : ''} ${row.value ? '' : 'text-slate-400'}`}>
                                                {display(f, row.value)}
                                            </div>
                                        )}
                                    </div>
                                    {mobileRow}
                                </React.Fragment>);
                            }
                            return (<React.Fragment key={f.key}>
                                <div className={`${f.full || f.kind === 'textarea' ? 'sm:col-span-2' : ''} ${flaggedCls} ${hideCls}`}>
                                    {row.verify_state === 'flagged' && stage !== 'ACKNOWLEDGED' && <RemarkBox text={row.remarks} />}
                                    {f.kind === 'doc' ? (
                                        <>
                                            <DocUpload id={id} allowUrl={assist} label={f.label} url={values[f.key] || null} required={isRequired(f.key)} hideOptional={!!g.required} error={err}
                                                mobile={mobile} folder={f.folder || 'kyc'} onChange={u => { set(f.key, u || ''); if (!u) setOcr(({ [f.key]: _, ...rest }) => rest); }}
                                                onFile={f.ocr ? file => readDoc(f.key, f.ocr!, file) : undefined} />
                                            {ocr[f.key] === 'reading' ? (
                                                <p className="mt-1.5 text-xs font-semibold text-slate-600 flex items-center gap-1.5" role="status"><Loader2 size={13} className="animate-spin" />Reading details from the image…</p>
                                            ) : ocr[f.key]?.length ? (
                                                <p className="mt-1.5 text-xs font-semibold text-emerald-800 flex items-start gap-1.5" role="status"><CheckCircle2 size={13} className="shrink-0 mt-px" />Filled in: {(ocr[f.key] as string[]).join(', ')}. Please check them below.</p>
                                            ) : ocr[f.key] ? (
                                                <p className="mt-1.5 text-xs font-semibold text-amber-800" role="status">Couldn’t read the details from this image. Please fill them in below.</p>
                                            ) : null}
                                        </>
                                    ) : (
                                        <>
                                            <Label htmlFor={id} required={isRequired(f.key)}>{f.label}</Label>
                                            {f.kind === 'select' ? (
                                                <>
                                                    {(f.options || []).length <= 3 && (
                                                        <div role="radiogroup" aria-label={f.label} className="lg:hidden grid gap-2" style={{ gridTemplateColumns: `repeat(${(f.options || []).length}, minmax(0, 1fr))` }}>
                                                            {(f.options || []).map(o => {
                                                                const on = values[f.key] === o;
                                                                return <button key={o} type="button" role="radio" aria-checked={on} onClick={() => set(f.key, o)}
                                                                    className={`h-12 max-lg:h-14 rounded-xl border text-[15px] font-bold transition ${on ? 'bg-[#1B4332] border-[#1B4332] text-white' : err ? 'bg-white border-red-400 text-slate-800' : 'bg-white border-slate-300 text-slate-800'}`}>{o}</button>;
                                                            })}
                                                        </div>
                                                    )}
                                                    <div className={(f.options || []).length <= 3 ? 'max-lg:hidden' : ''}>
                                                        <SelectField id={id} label={f.label} value={values[f.key] || ''} options={f.options || []} error={err}
                                                            placeholder={f.placeholder || `Select ${f.label.toLowerCase()}`} onChange={v => set(f.key, v)} />
                                                    </div>
                                                </>
                                            ) : f.kind === 'textarea' ? (
                                                <>
                                                    {!!f.sameAs?.length && (
                                                        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-2">
                                                            {f.sameAs.map(s => (
                                                                <label key={s.key} className="inline-flex items-center gap-2 min-h-[32px] text-[13px] max-lg:text-[14px] font-semibold text-slate-700 cursor-pointer select-none">
                                                                    <input type="checkbox" checked={linked[f.key] === s.key} onChange={e => toggleSame(f.key, s.key, e.target.checked)}
                                                                        className="w-[18px] h-[18px] rounded accent-[#1B4332]" />
                                                                    {s.label}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <textarea id={id} rows={3} value={values[f.key] || ''} placeholder={f.placeholder} readOnly={!!linked[f.key]}
                                                        onChange={e => set(f.key, e.target.value)} className={`${inputCls(err)} h-auto max-lg:h-auto py-3 max-lg:min-h-[96px] ${linked[f.key] ? 'bg-slate-50 text-slate-600' : ''}`} />
                                                </>
                                            ) : f.kind === 'date' ? (
                                                // DD-MM-YYYY on screen (the dashboard's one date format); YYYY-MM-DD in and out.
                                                <DatePicker value={values[f.key] || ''} onChange={v => set(f.key, v)} placeholder={f.placeholder || 'DD-MM-YYYY'}
                                                    yearsBack={100} align="left" accentColor="#1B4332" className="w-full"
                                                    buttonClassName={`${inputCls(err)} w-full justify-between font-normal text-[15px] sm:text-sm max-lg:text-base`} />
                                            ) : (
                                                <div className="relative">
                                                <input id={id} type={f.kind === 'email' ? 'email' : f.kind === 'account' && !reveal ? 'password' : 'text'}
                                                    inputMode={['aadhar', 'pincode', 'account'].includes(f.kind) ? 'numeric' : undefined}
                                                    autoComplete="off" value={values[f.key] || ''} placeholder={f.placeholder}
                                                    maxLength={f.maxLength}
                                                    onChange={e => set(f.key, sanitize(f.kind, e.target.value).slice(0, f.maxLength))}
                                                    className={`${inputCls(err)} ${['aadhar', 'pan', 'account', 'ifsc', 'pincode'].includes(f.kind) ? 'font-mono' : ''} ${f.kind === 'account' ? 'pr-12 tracking-widest' : ''}`} />
                                                {/* Account number is typed hidden (as dots); the eye toggles showing it. */}
                                                {f.kind === 'account' && (
                                                    <button type="button" onClick={() => setReveal(r => !r)} aria-label={reveal ? 'Hide account number' : 'Show account number'} aria-pressed={reveal}
                                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100">
                                                        {reveal ? <EyeOff size={18} /> : <Eye size={18} />}
                                                    </button>
                                                )}
                                                </div>
                                            )}
                                            {err ? <p className="mt-1 text-xs font-semibold text-red-700">{err}</p>
                                                : f.hint && !after ? <p className="mt-1 text-xs text-slate-500">{f.hint}</p> : null}
                                            {/* Extra content for this field (e.g. re-enter account number), inside its own cell on every screen. */}
                                            {after && <div className="mt-2.5">{after}</div>}
                                        </>
                                    )}
                                </div>
                                {mobileRow}
                            </React.Fragment>);
                        })}
                    </div>
                </section>
            ))}
            {extra && extra(values, set, editable)}
            <div className={`mt-8 pt-5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 ${nothingEditable ? '' : 'max-lg:fixed max-lg:bottom-0 max-lg:inset-x-0 max-lg:z-40 max-lg:m-0 max-lg:bg-white max-lg:border-slate-200 max-lg:px-4 max-lg:pt-3 max-lg:pb-[max(12px,env(safe-area-inset-bottom))] max-lg:gap-1.5'}`}>
                <div className="flex items-center gap-2 text-sm text-slate-500 max-lg:hidden">{footerLeft}</div>
                {!nothingEditable && (submit ? (
                    <button type="button" onClick={send} disabled={saving || left > 0}
                        className="w-full sm:w-auto max-lg:w-full min-h-[48px] max-lg:min-h-[52px] max-lg:rounded-2xl px-6 rounded-xl bg-[#1B4332] hover:bg-[#12301F] text-white font-bold inline-flex items-center justify-center gap-2 disabled:bg-[#1B4332]/40 disabled:cursor-not-allowed">
                        {saving && <Loader2 size={16} className="animate-spin" />}{submit.label}
                    </button>
                ) : (
                    <button type="button" onClick={save} disabled={saving || nothingEntered}
                        className="w-full sm:w-auto max-lg:w-full min-h-[48px] max-lg:min-h-[52px] max-lg:rounded-2xl px-6 rounded-xl bg-[#1B4332] hover:bg-[#12301F] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} className="max-lg:hidden" />}{saveLabel}
                    </button>
                ))}
                {!nothingEditable && <p className={`${submit ? '' : 'lg:hidden'} w-full text-center lg:text-right text-xs text-slate-500`}>{footNote}</p>}
            </div>
            {/* Room for the sticky save bar on mobile */}
            {!nothingEditable && <div className="lg:hidden h-20" />}
        </div>
    );
};

export default FieldSection;
